/**
 * Пакеты работ для бригады (ПЛ-38), ограничения пакета (ПЛ-39) и журнал ограничений проекта (ПЛ-40).
 * Ограничения по связям (ПСД, вопросы, фронт, акт, закупки) портал находит сам, остальное отмечает ПТО;
 * выдача — без открытых ограничений, иначе руководитель проекта с причиной. Пакет печатается.
 */
import {
  CONSTRAINT_KINDS,
  type ConstraintLogRow,
  MANUAL_CONSTRAINTS,
  type PackageConstraintRow,
  type PackageDetail,
  type PackageRow,
  type ScheduleView,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { Printer } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { LField } from './LField';

const dd = (s: string | null) => (s ? s.slice(0, 10).split('-').reverse().join('.') : '—');
const STATUS = { draft: 'Черновик', issued: 'Выдан бригаде', closed: 'Закрыт' } as const;
const kindTitle = (k: string) => CONSTRAINT_KINDS[k as keyof typeof CONSTRAINT_KINDS] ?? k;
const n1 = (x: number) => (Math.round(x * 10) / 10).toLocaleString('ru-RU');

function LinkTo({ c }: { c: PackageConstraintRow }) {
  if (!c.link) return <>{c.title}</>;
  const cls = 'inline-flex min-h-11 items-center hover:underline md:min-h-0';
  if (c.link.type === 'psd_set')
    return (
      <Link to="/psd/sets/$setId" params={{ setId: c.link.id }} className={cls}>
        {c.title}
      </Link>
    );
  if (c.link.type === 'psd_question')
    return (
      <Link to="/supervision/questions/$questionId" params={{ questionId: c.link.id }} className={cls}>
        {c.title}
      </Link>
    );
  if (c.link.type === 'inspection')
    return (
      <Link to="/supervision/inspections/$inspectionId" params={{ inspectionId: c.link.id }} className={cls}>
        {c.title}
      </Link>
    );
  return <>{c.title}</>;
}

export function PackagesPanel({ view }: { view: ScheduleView }) {
  const id = view.schedule.id;
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['packages', id],
    queryFn: () => api<PackageRow[]>(`/schedules/${id}/packages`),
  });
  const log = useQuery({
    queryKey: ['constraints', id],
    queryFn: () => api<ConstraintLogRow[]>(`/schedules/${id}/constraints`),
  });
  const [sel, setSel] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['packages', id] });
    void qc.invalidateQueries({ queryKey: ['constraints', id] });
    void qc.invalidateQueries({ queryKey: ['package'] });
  };
  return (
    <div className="flex flex-col gap-4">
      {/* При печати остаётся только карточка пакета */}
      <style>
        {
          '@media print { body * { visibility: hidden } .print-package, .print-package * { visibility: visible } .print-package { position: absolute; inset: 0 } }'
        }
      </style>
      <Panel
        title="Пакеты работ"
        meta="задание бригаде на 1–2 недели"
        actions={
          view.can.edit ? (
            <Button variant="primary" onClick={() => setCreating(true)} className="max-md:h-11">
              + Новый пакет
            </Button>
          ) : null
        }
      >
        {list.isPending ? <Spinner /> : null}
        {list.isError ? <ErrorBox error={list.error} /> : null}
        {list.data && !list.data.length ? (
          <EmptyState title="Пакетов пока нет">
            Пакет собирается из работ одного участка и вида работ на 1–2 недели: объём, трудоёмкость, листы
            ПСД, люди, техника и ограничения.
          </EmptyState>
        ) : null}
        <ul className="divide-y divide-line text-[14px]">
          {(list.data ?? []).map((w) => (
            <li key={w.id}>
              <button
                type="button"
                onClick={() => setSel(w.id === sel ? null : w.id)}
                className={clsx(
                  'flex min-h-11 w-full flex-wrap items-center justify-between gap-2 px-1 py-1.5 text-left hover:bg-surface-2',
                  w.id === sel && 'bg-surface-2',
                )}
              >
                <span className="min-w-0">
                  <span className="font-medium">
                    {w.number} {w.name}
                  </span>
                  <span className="block text-[12px] text-ink-2">
                    {dd(w.dateFrom)} — {dd(w.dateTo)}
                    {w.zoneName ? ` · ${w.zoneName}` : ''}
                    {w.workType ? ` · ${w.workType}` : ''}
                    {w.foremanName ? ` · бригадир ${w.foremanName}` : ''} · работ {w.items}
                  </span>
                </span>
                <span className="flex items-center gap-1">
                  {w.status !== 'closed' && w.openConstraints ? (
                    <Badge tone="danger">ограничений {w.openConstraints}</Badge>
                  ) : null}
                  <Badge tone={w.status === 'issued' ? 'success' : undefined}>{STATUS[w.status]}</Badge>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </Panel>
      {sel ? (
        <div className="print-package">
          <PackageCard key={sel} id={sel} view={view} onChanged={refresh} onDeleted={() => setSel(null)} />
        </div>
      ) : null}
      <Panel title="Журнал ограничений" meta="открытые по пакетам">
        {log.data && !log.data.length ? (
          <p className="text-[14px] text-ink-2">Открытых ограничений нет.</p>
        ) : null}
        <ul className="divide-y divide-line text-[14px]">
          {(log.data ?? []).slice(0, 200).map((c) => (
            <li
              key={`${c.packageId}|${c.id}`}
              className="flex flex-wrap items-center justify-between gap-2 py-1.5"
            >
              <span className="min-w-0">
                <LinkTo c={c} />
                <span className="block text-[12px] text-ink-2">
                  {c.packageNumber} {c.packageName} · {kindTitle(c.kind)}
                  {c.activityCode ? ` · ${c.activityCode}` : ''}
                  {c.responsibleName ? ` · ${c.responsibleName}` : ''}
                </span>
              </span>
              <span className={clsx('num text-[12px]', c.overdue ? 'text-danger' : 'text-ink-2')}>
                {c.dueDate ? `снять до ${dd(c.dueDate)}` : c.auto ? 'по связям' : 'без срока'}
              </span>
            </li>
          ))}
        </ul>
      </Panel>
      {creating ? (
        <CreateDialog
          view={view}
          onClose={() => setCreating(false)}
          onCreated={(pid) => {
            setCreating(false);
            setSel(pid);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateDialog({
  view,
  onClose,
  onCreated,
}: {
  view: ScheduleView;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const monday = (() => {
    const t = new Date();
    t.setUTCDate(t.getUTCDate() - ((t.getUTCDay() + 6) % 7) + 7);
    return t.toISOString().slice(0, 10);
  })();
  const plus = (d: string, k: number) => {
    const t = new Date(`${d}T00:00:00Z`);
    t.setUTCDate(t.getUTCDate() + k);
    return t.toISOString().slice(0, 10);
  };
  const [name, setName] = useState('');
  const [zone, setZone] = useState('');
  const [from, setFrom] = useState(monday);
  const [to, setTo] = useState(plus(monday, 11));
  const [foreman, setForeman] = useState('');
  const [pick, setPick] = useState<Set<string>>(new Set());
  const acts = view.activities.filter(
    (a) =>
      a.type === 'task' &&
      !a.actualFinish &&
      a.dates &&
      (!zone || a.zoneId === zone) &&
      a.dates.es.slice(0, 10) <= to &&
      a.dates.ef.slice(0, 10) >= from,
  );
  const m = useMutation({
    mutationFn: () =>
      api<PackageDetail>(`/schedules/${view.schedule.id}/packages`, {
        body: {
          name,
          zoneId: zone || null,
          foremanId: foreman || null,
          dateFrom: from,
          dateTo: to,
          activityIds: [...pick].filter((x) => acts.some((a) => a.id === x)),
        },
      }),
    onSuccess: (d) => onCreated(d.id),
    onError: (e) => toast(e.message, 'danger'),
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      width="lg"
      title="Новый пакет работ"
      description="Работы одного участка на 1–2 недели. Ограничения по связям портал найдёт сам."
      footer={
        <>
          <Button onClick={onClose} className="max-md:h-11">
            Отмена
          </Button>
          <Button
            variant="primary"
            disabled={!name.trim() || !pick.size}
            loading={m.isPending}
            onClick={() => m.mutate()}
            className="max-md:h-11"
          >
            Создать пакет
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-3">
          <LField label="Название">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например, «Бригада 2: стены камеры»"
              className="w-72 max-w-full max-md:h-11"
            />
          </LField>
          <LField label="С">
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="max-md:h-11"
            />
          </LField>
          <LField label="По">
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="max-md:h-11" />
          </LField>
          {view.zones.length ? (
            <LField label="Участок">
              <Select value={zone} onChange={(e) => setZone(e.target.value)} className="max-md:h-11">
                <option value="">Все</option>
                {view.zones.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.name}
                  </option>
                ))}
              </Select>
            </LField>
          ) : null}
          <LField label="Бригадир">
            <Select value={foreman} onChange={(e) => setForeman(e.target.value)} className="max-md:h-11">
              <option value="">Ответственный за работу</option>
              {view.people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </LField>
        </div>
        <ul className="max-h-80 divide-y divide-line overflow-y-auto text-[14px]">
          {acts.map((a) => (
            <li key={a.id}>
              <label className="flex min-h-11 items-center gap-2 py-1 md:min-h-0">
                <input
                  type="checkbox"
                  checked={pick.has(a.id)}
                  onChange={(e) => {
                    const n = new Set(pick);
                    if (e.target.checked) n.add(a.id);
                    else n.delete(a.id);
                    setPick(n);
                  }}
                />
                <span className="min-w-0">
                  {a.code} {a.name}
                  <span className="block text-[12px] text-ink-2">
                    {dd(a.dates!.es)} — {dd(a.dates!.ef)}
                    {a.workType ? ` · ${a.workType}` : ''}
                  </span>
                </span>
              </label>
            </li>
          ))}
          {!acts.length ? <li className="py-2 text-ink-2">На эти даты работ нет.</li> : null}
        </ul>
      </div>
    </Dialog>
  );
}

function PackageCard({
  id,
  view,
  onChanged,
  onDeleted,
}: {
  id: string;
  view: ScheduleView;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['package', id], queryFn: () => api<PackageDetail>(`/packages/${id}`) });
  const [kind, setKind] = useState<(typeof MANUAL_CONSTRAINTS)[number]>('permit');
  const [title, setTitle] = useState('');
  const [resp, setResp] = useState('');
  const [due, setDue] = useState('');
  const [reason, setReason] = useState('');
  const done = (d: PackageDetail) => {
    qc.setQueryData(['package', id], d);
    onChanged();
  };
  const call = useMutation({
    mutationFn: (x: { url: string; method?: 'POST' | 'PATCH' | 'DELETE'; body?: unknown }) =>
      api<PackageDetail>(x.url, { method: x.method ?? 'POST', body: x.body }),
    onSuccess: (d, x) => {
      if (x.method === 'DELETE' && x.url === `/packages/${id}`) {
        onDeleted();
        onChanged();
        return;
      }
      done(d);
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const d = q.data;
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  if (!d) return null;
  const open = d.constraints.filter((c) => c.open);
  return (
    <Panel
      title={`${d.number} ${d.name}`}
      meta={STATUS[d.status]}
      actions={
        <Button onClick={() => window.print()} className="max-md:h-11 print:hidden">
          <Printer size={16} /> Печать
        </Button>
      }
    >
      <div className="flex flex-col gap-4 text-[14px]">
        <p className="text-ink-2">
          {dd(d.dateFrom)} — {dd(d.dateTo)}
          {d.zoneName ? ` · ${d.zoneName}` : ''}
          {d.workType ? ` · ${d.workType}` : ''}
          {d.contractorName ? ` · ${d.contractorName}` : ''}
          {d.foremanName ? ` · бригадир ${d.foremanName}` : ''}
          {d.issuedAt ? ` · выдан ${dd(d.issuedAt)}${d.issuedByName ? `, ${d.issuedByName}` : ''}` : ''}
        </p>
        {d.exceptionReason ? (
          <p className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 text-[13px] text-danger">
            Выдан с открытыми ограничениями: {d.exceptionReason}
          </p>
        ) : null}
        <section>
          <h3 className="mb-1 font-semibold">Работы</h3>
          <ul className="divide-y divide-line">
            {d.activities.map((a) => (
              <li key={a.itemId} className="flex flex-wrap justify-between gap-2 py-1.5">
                <span>
                  {a.code} {a.name}
                </span>
                <span className="num text-[12px] text-ink-2">
                  {dd(a.es)} — {dd(a.ef)}
                  {a.quantity != null ? ` · ${n1(a.quantity)} ${a.unit ?? ''}` : ''}
                  {a.manHours ? ` · ${n1(a.manHours)} чел.-ч` : ''}
                  {a.percent ? ` · ${a.percent} %` : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
        {d.resources.length ? (
          <section>
            <h3 className="mb-1 font-semibold">Люди, техника и материалы</h3>
            <ul className="divide-y divide-line">
              {d.resources.map((r) => (
                <li key={`${r.kind}|${r.name}`} className="flex justify-between gap-2 py-1">
                  <span>{r.name}</span>
                  <span className="num text-ink-2">
                    {n1(r.total)} {r.unit}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        <section>
          <h3 className="mb-1 font-semibold">
            Ограничения
            {open.length ? <span className="text-danger"> · открыто {open.length}</span> : ' · все сняты'}
          </h3>
          <ul className="divide-y divide-line">
            {d.constraints.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                <label className="flex min-h-11 min-w-0 items-center gap-2 md:min-h-0">
                  <input
                    type="checkbox"
                    checked={!c.open}
                    disabled={!c.canToggle || call.isPending}
                    onChange={(e) =>
                      call.mutate({
                        url: `/package-constraints/${c.id}`,
                        method: 'PATCH',
                        body: { done: e.target.checked },
                      })
                    }
                  />
                  <span className={clsx('min-w-0', c.open && 'text-danger')}>
                    <LinkTo c={c} />
                    <span className="block text-[12px] text-ink-2">
                      {kindTitle(c.kind)}
                      {c.activityCode ? ` · ${c.activityCode}` : ''}
                      {c.auto ? ' · проверяется по связям' : ''}
                      {c.responsibleName ? ` · ${c.responsibleName}` : ''}
                    </span>
                  </span>
                </label>
                <span className={clsx('num text-[12px]', c.overdue ? 'text-danger' : 'text-ink-2')}>
                  {c.doneAt ? `снято ${dd(c.doneAt)}` : c.dueDate ? `до ${dd(c.dueDate)}` : ''}
                </span>
              </li>
            ))}
            {!d.constraints.length ? <li className="py-1.5 text-ink-2">Ограничений нет.</li> : null}
          </ul>
          {d.canEdit && d.status !== 'closed' ? (
            <div className="mt-2 flex flex-wrap items-end gap-2 print:hidden">
              <LField label="Вид">
                <Select
                  value={kind}
                  onChange={(e) => setKind(e.target.value as (typeof MANUAL_CONSTRAINTS)[number])}
                  className="max-md:h-11"
                >
                  {MANUAL_CONSTRAINTS.map((k) => (
                    <option key={k} value={k}>
                      {kindTitle(k)}
                    </option>
                  ))}
                </Select>
              </LField>
              <LField label="Что нужно">
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-64 max-w-full max-md:h-11"
                />
              </LField>
              <LField label="Ответственный">
                <Select value={resp} onChange={(e) => setResp(e.target.value)} className="max-md:h-11">
                  <option value="">—</option>
                  {view.people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </LField>
              <LField label="Снять до">
                <Input
                  type="date"
                  value={due}
                  onChange={(e) => setDue(e.target.value)}
                  className="max-md:h-11"
                />
              </LField>
              <Button
                disabled={!title.trim() || call.isPending}
                onClick={() => {
                  call.mutate({
                    url: `/packages/${id}/constraints`,
                    body: { kind, title, responsibleId: resp || null, dueDate: due || null },
                  });
                  setTitle('');
                }}
                className="max-md:h-11"
              >
                Добавить ограничение
              </Button>
            </div>
          ) : null}
        </section>
        {d.canEdit ? (
          <div className="flex flex-wrap items-end gap-2 print:hidden">
            {d.status === 'draft' ? (
              <>
                {open.length && d.canOverride ? (
                  <LField label="Причина выдачи с открытыми ограничениями">
                    <Input
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="w-72 max-w-full max-md:h-11"
                    />
                  </LField>
                ) : null}
                <Button
                  variant="primary"
                  disabled={
                    call.isPending ||
                    !d.activities.length ||
                    (open.length > 0 && (!d.canOverride || reason.trim().length < 3))
                  }
                  onClick={() =>
                    call.mutate({
                      url: `/packages/${id}/issue`,
                      body: open.length ? { exceptionReason: reason } : {},
                    })
                  }
                  className="max-md:h-11"
                >
                  Выдать бригаде
                </Button>
                {open.length && !d.canOverride ? (
                  <span className="text-[13px] text-ink-2">
                    Снимите ограничения — выдать с ними может только руководитель проекта.
                  </span>
                ) : null}
                <Button
                  variant="ghost"
                  disabled={call.isPending}
                  onClick={() =>
                    window.confirm(`Удалить пакет ${d.number}?`) &&
                    call.mutate({ url: `/packages/${id}`, method: 'DELETE' })
                  }
                  className="max-md:h-11"
                >
                  Удалить
                </Button>
              </>
            ) : d.status === 'issued' ? (
              <Button
                disabled={call.isPending}
                onClick={() => call.mutate({ url: `/packages/${id}/close` })}
                className="max-md:h-11"
              >
                Закрыть пакет
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
